import { DatePicker } from '@/components/ui/DatePicker'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Camera, Image as ImageIcon, User, X, Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react'
import { profilFotoYukle, profilFotoSil } from '@/lib/services/profil-foto-service'
import { MultiSelect } from '@/components/ui/MultiSelect'
import { useAuth } from '@/contexts/AuthContext'
import {
  personelOlustur,
  personelGuncelle,
  emailIleKullaniciVarMi,
  type PersonelOlusturmaData,
} from '@/lib/services/personel-service'
import {
  yoneticiYetkileriGuncelle,
  yoneticiPersonelEkle,
} from '@/lib/services/yonetici-service'
import { YETKI_GRUPLARI, YETKI_PRESETLERI, type YetkiId } from '@/lib/yetkiler'
import {
  usernameToEmail, emailToUsername, isValidLoginIdentifier,
  KULLANICI_DOMAIN, USERNAME_KURAL,
} from '@/lib/username'
import { tumPozisyonlariGetir } from '@/lib/services/pozisyon-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import type {
  Vardiyali, Pozisyon, Sube, IsoTarih,
} from '@/types'

interface Props {
  acik: boolean
  personel: Vardiyali | null
  onKapat: () => void
  onKaydedildi: (yeniPersonelId: string | null) => void
}


interface FormState {
  isim: string
  telefon: string
  email: string
  dogumTarihi: string
  iseBaslamaTarihi: string
  adres: string
  acilIsim: string
  acilTelefon: string
  pozisyonIdleri: string[]
  subeIdleri: string[]
  calismaTipi: 'fulltime' | 'parttime'
  takvimTipi: 'market' | 'ofis'
  maasTipi: 'saatlik' | 'aylik'
  ucret: string
  fazlaMesaiKatsayisi: '1' | '1.5' | '2'
  aylikStandartSaat: string
  sabitEkOdeme: string
  sabitKesinti: string
  extraMesaiYetkisi: boolean
  ibanNo: string
  telegramChatId: string
  yillikIzinBaslangicBakiye: string
  whatsappBildirimi: boolean
  // Yönetici yetkisi
  yoneticiMi: boolean
  sifre: string
  yoneticiYetkileri: YetkiId[]
}

const BOS: FormState = {
  isim: '',
  telefon: '',
  email: '',
  dogumTarihi: '',
  iseBaslamaTarihi: '',
  adres: '',
  acilIsim: '',
  acilTelefon: '',
  pozisyonIdleri: [],
  subeIdleri: [],
  calismaTipi: 'fulltime',
  takvimTipi: 'market',
  maasTipi: 'aylik',
  ucret: '',
  fazlaMesaiKatsayisi: '1.5',
  aylikStandartSaat: '300',
  sabitEkOdeme: '0',
  sabitKesinti: '0',
  extraMesaiYetkisi: false,
  ibanNo: '',
  telegramChatId: '',
  yillikIzinBaslangicBakiye: '0',
  whatsappBildirimi: true,
  yoneticiMi: false,
  sifre: '',
  yoneticiYetkileri: [],
}

function sadeceRakam(s: string): string {
  return s.replace(/[^0-9]/g, '')
}

const FIRMA_KURULUS = '2023-01-19'

export function PersonelFormModal({ acik, personel, onKapat, onKaydedildi }: Props) {
  const { firebaseUser, kullanici } = useAuth()
  const isBoss = kullanici?.rol === 'boss'
  const isMuhasebe = false

  const [form, setForm] = useState<FormState>(BOS)
  const [hatalar, setHatalar] = useState<Record<string, string>>({})
  const [hata, setHata] = useState<string | null>(null)
  const [kaydediliyor, setKaydediliyor] = useState(false)

  const [pozisyonlar, setPozisyonlar] = useState<Pozisyon[]>([])
  const [subeler, setSubeler] = useState<Sube[]>([])
  const [referansYukleniyor, setReferansYukleniyor] = useState(true)

  const cameraRef = useRef<HTMLInputElement>(null)
  const galeriRef = useRef<HTMLInputElement>(null)
  const [profilFoto, setProfilFoto] = useState<File | null>(null)
  const [profilOnizleme, setProfilOnizleme] = useState<string | null>(null)
  const [mevcutFotoUrl, setMevcutFotoUrl] = useState<string | null>(null)
  const [fotoyuKaldir, setFotoyuKaldir] = useState(false)
  const [sifreGorunur, setSifreGorunur] = useState(false)

  useEffect(() => {
    if (!acik) return
    setReferansYukleniyor(true)
    Promise.all([tumPozisyonlariGetir(), tumSubeleriGetir()])
      .then(([p, s]) => {
        setPozisyonlar(p.filter(x => x.aktif))
        setSubeler(s.filter(x => x.aktif))
      })
      .catch(err => {
        console.error(err)
        setHata('Pozisyon ve şube listeleri yüklenemedi.')
      })
      .finally(() => setReferansYukleniyor(false))
  }, [acik])

  useEffect(() => {
    if (personel) {
      const p = personel as Vardiyali & { profilFotoUrl?: string }
      setMevcutFotoUrl(p.profilFotoUrl ?? null)
      setProfilFoto(null)
      setProfilOnizleme(null)
      setFotoyuKaldir(false)
      const isYonetici = personel.rol === 'personel' && personel.yoneticiMi === true
      // Yönetici ise email'i username'e parse et (nur@shiftbeat.com → nur), değilse olduğu gibi
      const emailGoster = isYonetici
        ? emailToUsername(personel.email ?? '')
        : (personel.email ?? '')
      setForm({
        isim: personel.isim,
        telefon: sadeceRakam(personel.telefon),
        email: emailGoster,
        dogumTarihi: personel.dogumTarihi,
        iseBaslamaTarihi: personel.iseBaslamaTarihi,
        adres: personel.adres ?? '',
        acilIsim: personel.acilIletisim?.isim ?? '',
        acilTelefon: sadeceRakam(personel.acilIletisim?.telefon ?? ''),
        pozisyonIdleri: personel.pozisyonIdleri,
        subeIdleri: personel.subeIdleri,
        calismaTipi: personel.calismaTipi,
        takvimTipi: personel.takvimTipi,
        maasTipi: personel.maasTipi,
        ucret: personel.ucret.toString(),
        fazlaMesaiKatsayisi: personel.fazlaMesaiKatsayisi.toString() as '1' | '1.5' | '2',
        aylikStandartSaat: personel.aylikStandartSaat.toString(),
        sabitEkOdeme: personel.sabitEkOdeme.toString(),
        sabitKesinti: personel.sabitKesinti.toString(),
        extraMesaiYetkisi: personel.extraMesaiYetkisi === true,
        ibanNo: personel.ibanNo ?? '',
        telegramChatId: (personel as { telegramChatId?: string }).telegramChatId ?? '',
        yillikIzinBaslangicBakiye: personel.yillikIzinBaslangicBakiye.toString(),
        whatsappBildirimi: personel.whatsappBildirimi,
        yoneticiMi: isYonetici,
        sifre: '',
        yoneticiYetkileri: (personel.yoneticiYetkileri ?? []) as YetkiId[],
      })
    } else {
      setMevcutFotoUrl(null)
      setProfilFoto(null)
      setProfilOnizleme(null)
      setFotoyuKaldir(false)
      setForm(BOS)
    }
    setHatalar({})
    setHata(null)
  }, [personel, acik])

  // Muhasebe yeni personel oluşturamaz — uyarı
  const muhasebeYasak = isMuhasebe && !personel

  function dogrula(): boolean {
    const e: Record<string, string> = {}
    if (!form.isim.trim()) e.isim = 'Ad zorunlu'
    if (!form.telefon.trim()) e.telefon = 'Telefon zorunlu'
    if (!form.dogumTarihi) e.dogumTarihi = 'Doğum tarihi zorunlu'
    if (!form.iseBaslamaTarihi) {
      e.iseBaslamaTarihi = 'İşe başlama tarihi zorunlu'
    } else {
      if (form.iseBaslamaTarihi < FIRMA_KURULUS) {
        e.iseBaslamaTarihi = `Firma kuruluş tarihinden önce olamaz (${FIRMA_KURULUS})`
      } else if (form.dogumTarihi && form.iseBaslamaTarihi < form.dogumTarihi) {
        e.iseBaslamaTarihi = 'İşe başlama tarihi doğum tarihinden önce olamaz'
      }
    }
    if (!form.adres.trim()) e.adres = 'Adres zorunlu'
    if (form.pozisyonIdleri.length === 0) e.pozisyonIdleri = 'En az 1 pozisyon seç'
    if (form.subeIdleri.length === 0) e.subeIdleri = 'En az 1 şube seç'

    const ucret = parseFloat(form.ucret)
    if (isNaN(ucret) || ucret < 0) e.ucret = 'Geçerli bir ücret girin'

    const standartSaat = parseFloat(form.aylikStandartSaat)
    if (isNaN(standartSaat) || standartSaat <= 0) e.aylikStandartSaat = 'Geçerli sayı'

    // Yönetici doğrulamaları
    if (form.yoneticiMi) {
      if (!form.email.trim()) {
        e.email = 'Kullanıcı adı zorunlu'
      } else if (!isValidLoginIdentifier(form.email)) {
        e.email = USERNAME_KURAL
      }
      // Şifre: yeni hesap veya yetki yeni veriliyorsa zorunlu
      const yeniHesap = !personel
      const yetkiYeniVeriliyor = !!personel && !personel.yoneticiMi
      if ((yeniHesap || yetkiYeniVeriliyor) && !form.sifre) {
        e.sifre = 'Yönetici için şifre zorunlu (min 6)'
      }
      if (form.sifre && form.sifre.length < 6) e.sifre = 'Şifre en az 6 karakter olmalı'
      if (form.yoneticiYetkileri.length === 0) {
        e.yoneticiYetkileri = 'En az 1 yetki seçilmeli'
      }
    }

    setHatalar(e)
    return Object.keys(e).length === 0
  }

  /** Muhasebe için filtrelenmiş data — hassas alanlar gönderilmez */
  const muhasebeIcinFiltreliData = useMemo(() => (data: PersonelOlusturmaData): Partial<PersonelOlusturmaData> => {
    // Muhasebe sadece bu alanları güncelleyebilir:
    return {
      isim: data.isim,
      telefon: data.telefon,
      email: data.email,
      dogumTarihi: data.dogumTarihi,
      adres: data.adres,
      acilIletisim: data.acilIletisim,
      pozisyonIdleri: data.pozisyonIdleri,
      subeIdleri: data.subeIdleri,
      takvimTipi: data.takvimTipi,
      ibanNo: data.ibanNo,
      whatsappBildirimi: data.whatsappBildirimi,
    }
  }, [])

  async function kaydet(ev: React.FormEvent) {
    ev.preventDefault()
    if (!dogrula()) return
    if (!firebaseUser) {
      setHata('Oturum bulunamadı.')
      return
    }
    if (muhasebeYasak) {
      setHata('Yeni personel ekleme yetkisi sadece BOSS\'ta.')
      return
    }

    setKaydediliyor(true)
    setHata(null)

    // Username → email çevirisi (yönetici işaretliyse)
    const yoneticiEmail = form.yoneticiMi ? usernameToEmail(form.email) : ''

    // Firestore pre-check — aynı email aktif başka kullanıcıda var mı?
    // (Auth'un fetchSignInMethodsForEmail'i enumeration protection yüzünden işe yaramıyor)
    if (form.yoneticiMi && yoneticiEmail) {
      try {
        const cakisma = await emailIleKullaniciVarMi(yoneticiEmail, personel?.id)
        if (cakisma) {
          setHatalar(h => ({ ...h, email: 'Bu kullanıcı adı zaten kullanımda' }))
          setKaydediliyor(false)
          return
        }
      } catch {
        // Pre-check başarısız olursa atomik CF zaten yakalar
      }
    }

    try {
      const data: PersonelOlusturmaData = {
        isim: form.isim,
        telefon: form.telefon,
        // Yönetici ise tam email kayıt edilir, değilse opsiyonel
        email: form.yoneticiMi ? yoneticiEmail : (form.email.trim() || undefined),
        dogumTarihi: form.dogumTarihi as IsoTarih,
        iseBaslamaTarihi: form.iseBaslamaTarihi as IsoTarih,
        adres: form.adres.trim(),
        acilIletisim: form.acilIsim.trim()
          ? { isim: form.acilIsim.trim(), telefon: form.acilTelefon.trim() }
          : undefined,
        pozisyonIdleri: form.pozisyonIdleri,
        subeIdleri: form.subeIdleri,
        calismaTipi: form.calismaTipi,
        takvimTipi: form.takvimTipi,
        maasTipi: form.maasTipi,
        ucret: parseFloat(form.ucret),
        fazlaMesaiKatsayisi: parseFloat(form.fazlaMesaiKatsayisi) as 1 | 1.5 | 2,
        aylikStandartSaat: parseFloat(form.aylikStandartSaat),
        sabitEkOdeme: parseFloat(form.sabitEkOdeme) || 0,
        sabitKesinti: parseFloat(form.sabitKesinti) || 0,
        extraMesaiYetkisi: form.extraMesaiYetkisi,
        ibanNo: form.ibanNo.trim() || undefined,
        telegramChatId: form.telegramChatId.trim() || undefined,
        yillikIzinBaslangicBakiye: parseFloat(form.yillikIzinBaslangicBakiye) || 0,
        whatsappBildirimi: form.whatsappBildirimi,
      }

      const yapanRol: 'boss' | 'yonetici' = isBoss ? 'boss' : 'yonetici'
      const yapanIsim = kullanici?.isim

      let hedefId: string
      if (personel) {
        // Düzenleme
        const guncellemeData = isMuhasebe ? muhasebeIcinFiltreliData(data) : data
        await personelGuncelle(personel.id, guncellemeData, firebaseUser.uid, yapanRol, yapanIsim)
        hedefId = personel.id
        // Yönetici yetkisi senkronizasyonu — mevcut personel için
        if (isBoss) {
          const eskiYoneticiMi = (personel as Vardiyali | null)?.yoneticiMi === true
          if (form.yoneticiMi) {
            await yoneticiYetkileriGuncelle({
              uid: hedefId,
              yoneticiMi: true,
              yoneticiYetkileri: form.yoneticiYetkileri,
              email: yoneticiEmail,
              sifre: form.sifre || undefined,
            })
          } else if (eskiYoneticiMi) {
            await yoneticiYetkileriGuncelle({
              uid: hedefId,
              yoneticiMi: false,
            })
          }
        }
      } else if (form.yoneticiMi && isBoss) {
        // Yeni personel + yönetici — ATOMIK CF (Auth + Firestore tek seferde)
        hedefId = await yoneticiPersonelEkle({
          email: yoneticiEmail,
          sifre: form.sifre,
          yetkiler: form.yoneticiYetkileri,
          personel: {
            isim: data.isim.trim(),
            telefon: data.telefon.trim(),
            dogumTarihi: data.dogumTarihi,
            iseBaslamaTarihi: data.iseBaslamaTarihi,
            adres: data.adres,
            acilIletisim: data.acilIletisim,
            pozisyonIdleri: data.pozisyonIdleri,
            subeIdleri: data.subeIdleri,
            calismaTipi: data.calismaTipi,
            takvimTipi: data.takvimTipi,
            maasTipi: data.maasTipi,
            ucret: data.ucret,
            fazlaMesaiKatsayisi: data.fazlaMesaiKatsayisi,
            aylikStandartSaat: data.aylikStandartSaat,
            sabitEkOdeme: data.sabitEkOdeme,
            sabitKesinti: data.sabitKesinti,
            ibanNo: data.ibanNo,
            yillikIzinBaslangicBakiye: data.yillikIzinBaslangicBakiye,
            whatsappBildirimi: data.whatsappBildirimi,
          },
        })
      } else {
        // Yeni personel — yönetici değil, normal Firestore akışı
        hedefId = await personelOlustur(data, firebaseUser.uid, yapanRol, yapanIsim)
      }

      if (profilFoto) {
        try {
          await profilFotoYukle(hedefId, profilFoto)
        } catch (err) {
          console.error('Profil foto yükleme:', err)
        }
      } else if (fotoyuKaldir && personel) {
        try {
          await profilFotoSil(personel.id)
        } catch (err) {
          console.error('Profil foto silme:', err)
        }
      }

      onKaydedildi(personel ? null : hedefId)
    } catch (err) {
      console.error(err)
      const e = err as { message?: string; code?: string; details?: { message?: string } }
      const cfMsg = e.details?.message ?? e.message ?? ''
      if (cfMsg.includes('zaten kayıtlı') || e.code === 'functions/already-exists' || cfMsg.toLowerCase().includes('already')) {
        setHatalar(h => ({ ...h, email: 'Bu kullanıcı adı zaten kullanımda' }))
      } else {
        setHata('Kaydedilemedi: ' + (cfMsg || 'bilinmeyen hata'))
      }
    } finally {
      setKaydediliyor(false)
    }
  }

  return (
    <Modal
      acik={acik}
      onKapat={onKapat}
      baslik={personel ? 'Personeli Düzenle' : 'Yeni Personel'}
      boyut="lg"
    >
      {referansYukleniyor ? (
        <div className="py-8 text-center text-gray-500">Yükleniyor...</div>
      ) : muhasebeYasak ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-900">
          <p className="font-semibold mb-1">Yetersiz Yetki</p>
          <p className="text-sm">
            Yeni personel ekleme yetkisi sadece BOSS'ta. Mevcut personel bilgilerini düzenleyebilirsin.
          </p>
        </div>
      ) : (
        <form onSubmit={kaydet} className="space-y-6">
          {isMuhasebe && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
              <Lock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">Muhasebe Yetkisi</p>
                <p className="text-xs mt-0.5">
                  Maaş, işe başlama tarihi, çalışma tipi, yıllık izin bakiyesi ve sabit ek ödeme/kesinti alanları kilitlidir. Bu alanları yalnızca BOSS değiştirebilir.
                </p>
              </div>
            </div>
          )}

          {/* Yönetici yetkisi — sadece BOSS tarafından değiştirilebilir */}
          {isBoss && (
            <Bolum baslik="Yönetici Yetkisi">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={form.yoneticiMi}
                  onChange={e => setForm(f => ({ ...f, yoneticiMi: e.target.checked }))}
                  className="mt-0.5 w-5 h-5"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-brand-600" />
                    <span className="font-semibold text-gray-900">Bu kişiye yönetici yetkisi ver</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    BOSS paneline e-posta + şifre ile giriş yapabilir. Sorumlu/Muhasebe paneli yetkisi kazanır.
                    QR ile mesai/maaş/izin akışı aynı şekilde devam eder.
                  </p>
                </div>
              </label>

              {form.yoneticiMi && (
                <div className="mt-3 space-y-4 p-4 bg-brand-50/40 border border-brand-200 rounded-xl">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field
                      label="Kullanıcı Adı"
                      zorunlu
                      hata={hatalar.email}
                      yardim="Sade kullanıcı adı (örn. nur) ya da tam e-posta (örn. azra@gmail.com) yazabilirsin"
                    >
                      <div className="relative">
                        <input
                          type="text"
                          value={form.email}
                          onChange={e => setForm(f => ({ ...f, email: e.target.value.toLowerCase() }))}
                          className={`input-base ${!form.email.includes('@') ? 'pr-32' : ''}`}
                          autoComplete="off"
                          placeholder="nur veya azra@gmail.com"
                        />
                        {!form.email.includes('@') && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                            @{KULLANICI_DOMAIN}
                          </span>
                        )}
                      </div>
                    </Field>
                    <Field
                      label="Şifre"
                      zorunlu={!personel || !personel.yoneticiMi}
                      hata={hatalar.sifre}
                    >
                      <div className="relative">
                        <input
                          type={sifreGorunur ? 'text' : 'password'}
                          value={form.sifre}
                          onChange={e => setForm(f => ({ ...f, sifre: e.target.value }))}
                          className="input-base pr-10"
                          autoComplete="new-password"
                          minLength={6}
                          placeholder={personel?.yoneticiMi ? '••••••' : 'min 6 karakter'}
                        />
                        <button
                          type="button"
                          onClick={() => setSifreGorunur(v => !v)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                          tabIndex={-1}
                        >
                          {sifreGorunur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </Field>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-semibold text-gray-700">
                        Yetkiler {hatalar.yoneticiYetkileri && (
                          <span className="text-red-600 text-xs ml-1">({hatalar.yoneticiYetkileri})</span>
                        )}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, yoneticiYetkileri: [...YETKI_PRESETLERI.magazaSorumlusu] }))}
                          className="text-xs px-2 py-1 bg-white border border-gray-200 rounded hover:bg-gray-50"
                        >
                          Mağaza Sorumlusu seti
                        </button>
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, yoneticiYetkileri: [...YETKI_PRESETLERI.muhasebe] }))}
                          className="text-xs px-2 py-1 bg-white border border-gray-200 rounded hover:bg-gray-50"
                        >
                          Muhasebe seti
                        </button>
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, yoneticiYetkileri: [] }))}
                          className="text-xs px-2 py-1 bg-white border border-gray-200 rounded hover:bg-gray-50 text-red-600"
                        >
                          Temizle
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">
                      Yetkiler personelin atandığı <strong>şubelerle sınırlı</strong>. BOSS sonradan değiştirebilir.
                    </p>
                    <div className="space-y-2">
                      {YETKI_GRUPLARI.map(grup => (
                        <YetkiGrubuUI
                          key={grup.baslik}
                          grup={grup}
                          secili={form.yoneticiYetkileri}
                          onChange={(yetki, ekle) => {
                            setForm(f => ({
                              ...f,
                              yoneticiYetkileri: ekle
                                ? [...f.yoneticiYetkileri, yetki]
                                : f.yoneticiYetkileri.filter(y => y !== yetki),
                            }))
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </Bolum>
          )}

          <Bolum baslik="Kişisel Bilgiler">
            <div className="flex flex-col items-center gap-4 mb-5 p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
              <p className="text-sm font-semibold text-gray-700 self-start">
                Profil Fotoğrafı
              </p>

              <div className="relative">
                {profilOnizleme || (mevcutFotoUrl && !fotoyuKaldir) ? (
                  <img
                    src={profilOnizleme ?? mevcutFotoUrl ?? ''}
                    alt="Profil"
                    className="w-72 h-72 sm:w-80 sm:h-80 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="w-72 h-72 sm:w-80 sm:h-80 rounded-full bg-gradient-to-br from-brand-400 to-indigo-500 flex items-center justify-center border-4 border-white shadow-lg">
                    <User className="w-32 h-32 sm:w-36 sm:h-36 text-white" />
                  </div>
                )}
                {(profilFoto || (mevcutFotoUrl && !fotoyuKaldir)) && (
                  <button
                    type="button"
                    onClick={() => {
                      setProfilFoto(null)
                      setProfilOnizleme(null)
                      if (mevcutFotoUrl) setFotoyuKaldir(true)
                    }}
                    className="absolute -top-1 -right-1 w-12 h-12 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center shadow-lg transition-colors"
                    title="Kaldır"
                  >
                    <X className="w-6 h-6" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
                <button
                  type="button"
                  onClick={() => cameraRef.current?.click()}
                  className="inline-flex items-center justify-center gap-1.5 px-5 py-5 bg-white border-2 border-gray-300 rounded-xl text-base font-semibold hover:border-brand-500 hover:bg-brand-50 transition-colors"
                >
                  <Camera className="w-8 h-8" /> Kamera
                </button>
                <button
                  type="button"
                  onClick={() => galeriRef.current?.click()}
                  className="inline-flex items-center justify-center gap-1.5 px-5 py-5 bg-white border-2 border-gray-300 rounded-xl text-base font-semibold hover:border-brand-500 hover:bg-brand-50 transition-colors"
                >
                  <ImageIcon className="w-8 h-8" /> Galeri
                </button>
              </div>
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="user"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (!f) return
                  if (f.size > 5 * 1024 * 1024) {
                    setHata('Fotoğraf 5 MB\'dan büyük olamaz')
                    return
                  }
                  setProfilFoto(f)
                  setFotoyuKaldir(false)
                  const r = new FileReader()
                  r.onload = (ev) => setProfilOnizleme(ev.target?.result as string)
                  r.readAsDataURL(f)
                }}
              />
              <input
                ref={galeriRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (!f) return
                  if (f.size > 5 * 1024 * 1024) {
                    setHata('Fotoğraf 5 MB\'dan büyük olamaz')
                    return
                  }
                  setProfilFoto(f)
                  setFotoyuKaldir(false)
                  const r = new FileReader()
                  r.onload = (ev) => setProfilOnizleme(ev.target?.result as string)
                  r.readAsDataURL(f)
                }}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Ad Soyad" zorunlu hata={hatalar.isim}>
                <input
                  type="text"
                  value={form.isim}
                  onChange={e => setForm(f => ({ ...f, isim: e.target.value }))}
                  className="input-base"
                  placeholder="Örn: Ahmet Yılmaz"
                />
              </Field>
              <Field label="Telefon" zorunlu hata={hatalar.telefon}>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={form.telefon}
                  onChange={e => setForm(f => ({ ...f, telefon: sadeceRakam(e.target.value) }))}
                  className="input-base"
                  placeholder="905333334444"
                />
              </Field>
              {!form.yoneticiMi && (
                <Field label="E-posta (opsiyonel)">
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="input-base"
                  />
                </Field>
              )}
              <Field label="Doğum Tarihi" zorunlu hata={hatalar.dogumTarihi}>
                <DatePicker
                  value={form.dogumTarihi}
                  onChange={(v) => setForm(f => ({ ...f, dogumTarihi: v }))}
                />
              </Field>
              <Field
                label="İşe Başlama"
                zorunlu
                hata={hatalar.iseBaslamaTarihi}
                bossOnly={isMuhasebe}
              >
                <DatePicker
                  value={form.iseBaslamaTarihi}
                  onChange={(v) => setForm(f => ({ ...f, iseBaslamaTarihi: v }))}
                  min={FIRMA_KURULUS}
                  disabled={isMuhasebe}
                />
              </Field>
              <Field label="Adres" zorunlu hata={hatalar.adres}>
                <input
                  type="text"
                  value={form.adres}
                  onChange={e => setForm(f => ({ ...f, adres: e.target.value }))}
                  className="input-base"
                  placeholder="Mahalle, sokak, şehir"
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <Field label="Acil İletişim — Telefon (opsiyonel)">
                <input
                  type="tel"
                  inputMode="numeric"
                  value={form.acilTelefon}
                  onChange={e => setForm(f => ({ ...f, acilTelefon: sadeceRakam(e.target.value) }))}
                  className="input-base"
                  placeholder="905333334444"
                />
              </Field>
              <Field label="Acil İletişim — İsim (opsiyonel)">
                <input
                  type="text"
                  value={form.acilIsim}
                  onChange={e => setForm(f => ({ ...f, acilIsim: e.target.value }))}
                  className="input-base"
                  placeholder="Örn: Anne / Eş"
                />
              </Field>
            </div>
          </Bolum>

          <Bolum baslik="Çalışma Profili">
            <Field label="Pozisyonlar" zorunlu hata={hatalar.pozisyonIdleri}>
              <MultiSelect
                secenekler={pozisyonlar.map(p => ({ id: p.id, label: p.isim }))}
                secili={form.pozisyonIdleri}
                onChange={ids => setForm(f => ({ ...f, pozisyonIdleri: ids }))}
                placeholder="Pozisyon seç (birden fazla seçilebilir)"
                bos="Henüz pozisyon yok. Önce Pozisyonlar sayfasından ekle."
              />
            </Field>
            <Field label="Şubeler" zorunlu hata={hatalar.subeIdleri}>
              <MultiSelect
                secenekler={subeler.map(s => ({ id: s.id, label: s.isim, alt: s.adres }))}
                secili={form.subeIdleri}
                onChange={ids => setForm(f => ({ ...f, subeIdleri: ids }))}
                placeholder="Şube seç"
                bos="Henüz şube yok. Önce Şubeler sayfasından ekle."
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Çalışma Tipi" bossOnly={isMuhasebe}>
                <select
                  value={form.calismaTipi}
                  onChange={e => setForm(f => ({ ...f, calismaTipi: e.target.value as 'fulltime' | 'parttime' }))}
                  className="input-base"
                  disabled={isMuhasebe}
                >
                  <option value="fulltime">Tam Zamanlı (yıllık izin hak eder)</option>
                  <option value="parttime">Yarı Zamanlı</option>
                </select>
              </Field>
              <Field label="Çalışma Takvimi">
                <select
                  value={form.takvimTipi}
                  onChange={e => setForm(f => ({ ...f, takvimTipi: e.target.value as 'market' | 'ofis' }))}
                  className="input-base"
                >
                  <option value="market">Market (vardiyalı, 7/24)</option>
                  <option value="ofis">Ofis (Pzt-Cmt, Pazar tatil)</option>
                </select>
              </Field>
            </div>
          </Bolum>

          <Bolum baslik="Maaş Bilgileri">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Maaş Tipi" bossOnly={isMuhasebe}>
                <select
                  value={form.maasTipi}
                  onChange={e => setForm(f => ({ ...f, maasTipi: e.target.value as 'saatlik' | 'aylik' }))}
                  className="input-base"
                  disabled={isMuhasebe}
                >
                  <option value="aylik">Aylık (sabit)</option>
                  <option value="saatlik">Saatlik</option>
                </select>
              </Field>
              <Field
                label={form.maasTipi === 'aylik' ? 'Aylık Brüt (₺)' : 'Saatlik Ücret (₺)'}
                zorunlu
                hata={hatalar.ucret}
                bossOnly={isMuhasebe}
              >
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={form.ucret}
                  onChange={e => setForm(f => ({ ...f, ucret: e.target.value }))}
                  className="input-base"
                  placeholder={form.maasTipi === 'aylik' ? '30000' : '100'}
                  disabled={isMuhasebe}
                />
              </Field>
              <Field label="Fazla Mesai Katsayısı" bossOnly={isMuhasebe}>
                <select
                  value={form.fazlaMesaiKatsayisi}
                  onChange={e => setForm(f => ({ ...f, fazlaMesaiKatsayisi: e.target.value as '1' | '1.5' | '2' }))}
                  className="input-base"
                  disabled={isMuhasebe}
                >
                  <option value="1">x1 (yok)</option>
                  <option value="1.5">x1.5 (standart)</option>
                  <option value="2">x2 (yüksek)</option>
                </select>
              </Field>
              <Field label="Aylık Standart Saat" hata={hatalar.aylikStandartSaat} bossOnly={isMuhasebe}>
                <input
                  type="number"
                  value={form.aylikStandartSaat}
                  onChange={e => setForm(f => ({ ...f, aylikStandartSaat: e.target.value }))}
                  className="input-base"
                  disabled={isMuhasebe}
                />
              </Field>
              <Field label="Sabit Ek Ödeme (yol/yemek ₺)" bossOnly={isMuhasebe}>
                <input
                  type="number"
                  value={form.sabitEkOdeme}
                  onChange={e => setForm(f => ({ ...f, sabitEkOdeme: e.target.value }))}
                  className="input-base"
                  disabled={isMuhasebe}
                />
              </Field>
              <Field label="Sabit Kesinti (SGK/vergi ₺)" bossOnly={isMuhasebe}>
                <input
                  type="number"
                  value={form.sabitKesinti}
                  onChange={e => setForm(f => ({ ...f, sabitKesinti: e.target.value }))}
                  className="input-base"
                  disabled={isMuhasebe}
                />
              </Field>
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <input
                type="checkbox"
                checked={form.extraMesaiYetkisi}
                onChange={e => setForm(f => ({ ...f, extraMesaiYetkisi: e.target.checked }))}
                disabled={isMuhasebe}
                className="mt-0.5 w-4 h-4"
              />
              <div className="flex-1">
                <span className="text-sm font-semibold text-gray-800">
                  Extra mesai yetkisi
                </span>
                <p className="text-xs text-gray-600 mt-0.5">
                  Açıksa: 10 saat / vardiya bitişi tavanı kalkar, fazla mesai ödenir.
                  Kapalıysa: vardiya bitişinden sonrası ödenmez.
                </p>
              </div>
            </label>
          </Bolum>

          <Bolum baslik="Banka & İzin">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="IBAN (opsiyonel)">
                <input
                  type="text"
                  value={form.ibanNo}
                  onChange={e => setForm(f => ({ ...f, ibanNo: e.target.value }))}
                  className="input-base"
                  placeholder="TR..."
                />
              </Field>
              <Field
                label="Mevcut Yıllık İzin Bakiyesi (gün)"
                yardim="Eski sistemden geçişte kalan izin günleri (yeni personel için 0)"
                bossOnly={isMuhasebe}
              >
                <input
                  type="number"
                  value={form.yillikIzinBaslangicBakiye}
                  onChange={e => setForm(f => ({ ...f, yillikIzinBaslangicBakiye: e.target.value }))}
                  className="input-base"
                  disabled={isMuhasebe}
                />
              </Field>
              <Field
                label="Telegram Chat ID (opsiyonel)"
                yardim="@userinfobot'tan alınır. Kritik bildirimler Telegram'a yollanır."
              >
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.telegramChatId}
                  onChange={e => setForm(f => ({ ...f, telegramChatId: e.target.value }))}
                  className="input-base font-mono"
                  placeholder="123456789"
                />
              </Field>
            </div>
          </Bolum>

          <Bolum baslik="Bildirim Tercihi">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.whatsappBildirimi}
                onChange={e => setForm(f => ({ ...f, whatsappBildirimi: e.target.checked }))}
              />
              <span className="text-sm text-gray-700">
                WhatsApp bildirimleri gönder (vardiya, izin, görev vb.)
              </span>
            </label>
          </Bolum>

          {hata && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
              {hata}
            </div>
          )}

          <div className="flex gap-2 pt-2 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onKapat}
              className="btn-secondary flex-1"
              disabled={kaydediliyor}
            >
              İptal
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={kaydediliyor}>
              {kaydediliyor
                ? 'Kaydediliyor...'
                : personel
                  ? 'Güncelle'
                  : form.yoneticiMi
                    ? 'Personeli + Yönetici Hesabını Oluştur'
                    : 'Ekle ve QR Oluştur'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

interface YetkiGrubuUIProps {
  grup: typeof YETKI_GRUPLARI[number]
  secili: YetkiId[]
  onChange: (yetki: YetkiId, ekle: boolean) => void
}

function YetkiGrubuUI({ grup, secili, onChange }: YetkiGrubuUIProps) {
  const tumYetkiIdleri = grup.yetkiler.map(y => y.id)
  const seciliSayisi = tumYetkiIdleri.filter(id => secili.includes(id)).length
  const hepsiSecili = seciliSayisi === tumYetkiIdleri.length
  const hicSecili = seciliSayisi === 0

  return (
    <details className="bg-white border border-gray-200 rounded-lg" open={!hicSecili}>
      <summary className="px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-50">
        <span className="text-sm font-semibold text-gray-800">
          {grup.baslik}
          <span className="ml-2 text-xs font-normal text-gray-500">
            {seciliSayisi} / {tumYetkiIdleri.length}
          </span>
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            tumYetkiIdleri.forEach(id => onChange(id, !hepsiSecili))
          }}
          className="text-xs px-2 py-0.5 bg-gray-100 hover:bg-gray-200 rounded"
        >
          {hepsiSecili ? 'Tümünü kapat' : 'Tümünü aç'}
        </button>
      </summary>
      <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-gray-100">
        {grup.yetkiler.map(y => (
          <label key={y.id} className="flex items-start gap-2 cursor-pointer py-1 hover:bg-gray-50 rounded px-1">
            <input
              type="checkbox"
              checked={secili.includes(y.id)}
              onChange={e => onChange(y.id, e.target.checked)}
              className="mt-0.5 w-4 h-4"
            />
            <span className="text-sm text-gray-700 flex-1">{y.label}</span>
          </label>
        ))}
      </div>
    </details>
  )
}

interface BolumProps {
  baslik: string
  children: React.ReactNode
}
function Bolum({ baslik, children }: BolumProps) {
  return (
    <div>
      <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
        {baslik}
      </h3>
      {children}
    </div>
  )
}

interface FieldProps {
  label: string
  hata?: string
  yardim?: string
  zorunlu?: boolean
  bossOnly?: boolean
  children: React.ReactNode
}
function Field({ label, hata, yardim, zorunlu, bossOnly, children }: FieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label} {zorunlu && <span className="text-red-500">*</span>}
        {bossOnly && (
          <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full font-bold ml-1.5 align-middle">
            <Lock className="w-2.5 h-2.5" />
            BOSS
          </span>
        )}
      </label>
      {children}
      {yardim && !hata && <p className="text-xs text-gray-500 mt-1">{yardim}</p>}
      {hata && <p className="text-xs text-red-600 mt-1">{hata}</p>}
    </div>
  )
}
